import { LayoutDashboard, Trash2, UserPlus, Users } from '@tamagui/lucide-icons';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Input } from '@unistocks/components/input/input';
import { Column } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Paginator } from '@unistocks/components/paginator/paginator';
import { asyncStateTransition } from '@unistocks/components/query-state/async-state';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { getLocation, Table } from '@unistocks/components/table/table';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { CreateUserSheet } from '@unistocks/features/admin/components/user-management/create-user-sheet';
import { IdentityUserDto } from '@unistocks/proxy/axios-client';
import {
  userGetListQueryKey,
  useUserDeleteMutationWithParameters,
  useUserGetListQuery
} from '@unistocks/proxy/axios-client/UserQuery';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { usePermission } from '@unistocks/shared/hooks/use-permission';
import { t } from '@unistocks/shared/localization/index.ts';
import { UnistocksPermissions } from '@unistocks/shared/permissions';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'tamagui';

export const UserList: React.FC = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);

  const hasDeleteUser = usePermission(UnistocksPermissions.UserManagementDelete);
  const hasCreateUser = usePermission(UnistocksPermissions.UserManagementCreate);

  const { data, isSuccess, isRefetching, refetch } = useUserGetListQuery(
    searchText || undefined,
    undefined,
    (currentPage - 1) * MAX_RESULT_COUNT,
    MAX_RESULT_COUNT,
    undefined,
    { placeholderData: keepPreviousData }
  );

  const { mutate: deleteUser, isPending: isDeleting } = useUserDeleteMutationWithParameters({
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: userGetListQueryKey() });
      setDeletingId(null);
    },
    onError: () => {
      toast.error(t('AbpUi::DefaultErrorMessage'));
      setDeletingId(null);
    }
  });

  const users: IdentityUserDto[] = isSuccess ? data.items : [];

  const handleSearch = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const handleDelete = (userId: string) => {
    setDeletingId(userId);
    deleteUser({ id: userId });
  };

  const handleUserCreated = () => {
    setCurrentPage(1);
    queryClient.invalidateQueries({ queryKey: userGetListQueryKey() });
  };

  return (
    <Page size="medium">
      <Page.ScrollView
        headerTitle={t('AbpIdentity::Users')}
        headerIcon={
          hasCreateUser ? (
            <Button size={'$3'} Icon={UserPlus} onPress={() => setCreateUserOpen(true)}>
              {t('AbpIdentity::NewUser')}
            </Button>
          ) : undefined
        }
        refreshing={isRefetching}
        onRefresh={refetch}
        breadcrumbs={[
          {
            key: 'admin',
            href: '/(admin)',
            name: t('::Menu:Administration'),
            rootPathName: 'admin',
            Icon: props => <LayoutDashboard {...props} />
          }
        ]}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Column gap={'$3'} mt={'$3'}>
          <Input
            placeholder={t('AbpUi::PagerSearch')}
            value={searchText}
            onChangeText={handleSearch}
            $md={{
              size: '$3'
            }}
          />

          <Table
            transition="quickerLessBouncy"
            animateOnly={['opacity', 'transform']}
            enterStyle={{ o: 0, y: -20 }}
          >
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell cellLocation="first">
                  <Table.HeaderCellText>{t('AbpIdentity::UserName')}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell cellLocation="middle">
                  <Table.HeaderCellText>{t('AbpAccount::EmailAddress')}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell cellLocation="middle" alignHeaderCells={{ x: 'end' }}>
                  <Table.HeaderCellText>
                    {t('AbpIdentity::DisplayName:IsActive')}
                  </Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell cellLocation="last" alignHeaderCells={{ x: 'end' }}>
                  <Table.HeaderCellText>{t('AbpUi::Actions')}</Table.HeaderCellText>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>

            <QueryState
              queryKeys={[
                userGetListQueryKey(
                  searchText || undefined,
                  undefined,
                  (currentPage - 1) * MAX_RESULT_COUNT,
                  MAX_RESULT_COUNT,
                  undefined
                )
              ]}
              loadingText={t('AbpUi::LoadingWithThreeDot')}
              isEmpty={isSuccess && !users.length}
              noDataIcon={Users}
              noDataText={t('::NoDataAvailableInDatatable')}
            >
              <Table.Body key={'table-body'} {...asyncStateTransition}>
                {users.map((user, i) => (
                  <Table.Row
                    key={user.id}
                    pressable
                    rowLocation={getLocation(i, users)}
                    onPress={() =>
                      router.push({
                        pathname: '/(admin)/user-management/[id]',
                        params: { id: user.id }
                      })
                    }
                  >
                    <Table.Cell cellLocation="first">
                      <Paragraph>{user.userName}</Paragraph>
                    </Table.Cell>
                    <Table.Cell cellLocation="middle">
                      <Paragraph>{user.email}</Paragraph>
                    </Table.Cell>
                    <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }}>
                      <View
                        px={'$2'}
                        py={'$1'}
                        borderRadius={'$2'}
                        bg={user.isActive ? '$green8' : '$red8'}
                      >
                        <Paragraph color={'$white1'} size={'$3'}>
                          {user.isActive
                            ? t('AbpIdentity::DisplayName:IsActive')
                            : t('AbpIdentity::DisplayName:IsInActive')}
                        </Paragraph>
                      </View>
                    </Table.Cell>
                    <Table.ActionsCell>
                      {hasDeleteUser && (
                        <Confirm
                          title={t('::DeleteWhat', { 0: user.userName })}
                          message={t('::UserManagement:DeleteUser:Message', { 0: user.userName })}
                          onConfirm={() => handleDelete(user.id)}
                          isLoading={isDeleting && deletingId === user.id}
                        >
                          <Button
                            size={'$2'}
                            theme="red"
                            minW={'$4'}
                            Icon={Trash2}
                            loading={isDeleting && deletingId === user.id}
                          />
                        </Confirm>
                      )}
                    </Table.ActionsCell>
                  </Table.Row>
                ))}
              </Table.Body>

              <Table.Foot key={'table-foot'} {...asyncStateTransition}>
                <Table.Row>
                  <Table.Cell alignCells={{ x: 'center' }}>
                    <Paginator
                      flex={1}
                      itemsPerPage={MAX_RESULT_COUNT}
                      currentPage={currentPage}
                      totalItems={data?.totalCount ?? 0}
                      onPageChange={setCurrentPage}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Foot>
            </QueryState>
          </Table>
        </Column>
      </Page.ScrollView>

      <CreateUserSheet
        open={createUserOpen}
        onOpenChange={setCreateUserOpen}
        onCreated={handleUserCreated}
      />
    </Page>
  );
};
